/**
 * Clipboard Utilities
 * Functions for copy/paste operations that preserve character-level styling
 *
 * The html_text structure:
 * {
 *   text: "Hello World",
 *   charStyleOverrides: [0, 0, "1:2", "1:2", 0, ...],  // style ID per character
 *   styleOverrides: [{ id: "1:2", css: { "font-size": "24px", color: "#ff0000" } }],
 *   css: { "font-family": "Arial", ... }  // base styles
 * }
 */

/**
 * Extract text and styles from a range within html_text
 * @param {Object} htmlText - The full html_text object
 * @param {number} startOffset - Start character position (inclusive)
 * @param {number} endOffset - End character position (exclusive)
 * @returns {Object} Extracted text with styles
 */
export function extractTextAndStyles(htmlText, startOffset, endOffset) {
  if (!htmlText || typeof htmlText.text !== 'string') {
    return {
      text: '',
      charStyleOverrides: [],
      styleOverrides: [],
      css: {}
    };
  }

  // Clamp offsets to valid range
  const start = Math.max(0, startOffset);
  const end = Math.min(htmlText.text.length, endOffset);

  if (start >= end) {
    return {
      text: '',
      charStyleOverrides: [],
      styleOverrides: [],
      css: htmlText.css || {}
    };
  }

  // Extract the text slice
  const text = htmlText.text.slice(start, end);

  // Extract the charStyleOverrides slice
  const charOverrides = htmlText.charStyleOverrides?.slice(start, end) || [];

  // Find which style IDs are actually used in this slice
  const usedStyleIds = new Set();
  charOverrides.forEach(id => {
    if (id && id !== 0) {
      usedStyleIds.add(id);
    }
  });

  // Filter styleOverrides to only include used styles
  const styleOverrides = (htmlText.styleOverrides || []).filter(
    style => usedStyleIds.has(style.id)
  );

  return {
    text,
    charStyleOverrides: charOverrides,
    styleOverrides,
    css: htmlText.css || {}
  };
}

/**
 * Generate a new unique style ID that doesn't conflict with existing ones
 * @param {Array} existingOverrides - Current styleOverrides array
 * @param {string} baseId - The original style ID to remap
 * @returns {string} New unique style ID
 */
function generateUniqueStyleId(existingOverrides, baseId) {
  const existingIds = new Set(existingOverrides.map(s => s.id));

  // If the ID doesn't exist, we can use it as-is
  if (!existingIds.has(baseId)) {
    return baseId;
  }

  // Generate a new ID by appending a suffix
  let counter = 1;
  let newId = `${baseId}_p${counter}`;
  while (existingIds.has(newId)) {
    counter++;
    newId = `${baseId}_p${counter}`;
  }

  return newId;
}

/**
 * Merge clipboard content into existing text state at a given position
 * Handles style ID remapping to avoid conflicts
 *
 * @param {Object} currentState - Current html_text state
 * @param {Object} pastedData - Data from clipboard (same structure as html_text)
 * @param {number} position - Insert position (caret position)
 * @param {number} deleteCount - Number of characters to delete (for replacing selection)
 * @returns {Object} New merged html_text state
 */
export function mergeClipboardContent(currentState, pastedData, position, deleteCount = 0) {
  // Handle null/undefined current state
  const current = {
    text: currentState?.text || '',
    charStyleOverrides: currentState?.charStyleOverrides || [],
    styleOverrides: currentState?.styleOverrides || [],
    css: currentState?.css || {}
  };

  // Handle plain text paste (no style data)
  const pasted = {
    text: pastedData?.text || '',
    charStyleOverrides: pastedData?.charStyleOverrides || [],
    styleOverrides: pastedData?.styleOverrides || [],
    css: pastedData?.css || {}
  };

  if (!pasted.text) {
    return current;
  }

  // Ensure position is valid
  const insertPos = Math.max(0, Math.min(position, current.text.length));
  const delCount = Math.max(0, Math.min(deleteCount, current.text.length - insertPos));

  // 1. Merge the text
  const newText =
    current.text.slice(0, insertPos) +
    pasted.text +
    current.text.slice(insertPos + delCount);

  // 2. Build style ID remapping to avoid conflicts
  const idMapping = new Map(); // old ID -> new ID
  const mergedStyleOverrides = [...current.styleOverrides];

  pasted.styleOverrides.forEach(pastedStyle => {
    // Check if this exact style already exists in current
    const existingStyle = current.styleOverrides.find(
      s => s.id === pastedStyle.id && JSON.stringify(s.css) === JSON.stringify(pastedStyle.css)
    );

    if (existingStyle) {
      // Same ID and same CSS - reuse it
      idMapping.set(pastedStyle.id, pastedStyle.id);
    } else {
      // Need to add the style, possibly with a new ID
      const newId = generateUniqueStyleId(mergedStyleOverrides, pastedStyle.id);
      idMapping.set(pastedStyle.id, newId);
      mergedStyleOverrides.push({
        id: newId,
        css: { ...pastedStyle.css }
      });
    }
  });

  // 3. Remap pasted charStyleOverrides to use new IDs
  const remappedPastedOverrides = pasted.charStyleOverrides.map(id => {
    if (!id || id === 0) return 0;
    return idMapping.get(id) || id;
  });

  // Ensure pasted overrides array matches pasted text length
  while (remappedPastedOverrides.length < pasted.text.length) {
    remappedPastedOverrides.push(0);
  }

  // 4. Merge charStyleOverrides arrays
  const beforeOverrides = current.charStyleOverrides.slice(0, insertPos);
  const afterOverrides = current.charStyleOverrides.slice(insertPos + delCount);

  // Ensure before array is properly sized
  while (beforeOverrides.length < insertPos) {
    beforeOverrides.push(0);
  }

  const newCharStyleOverrides = [
    ...beforeOverrides,
    ...remappedPastedOverrides,
    ...afterOverrides
  ];

  // 5. Clean up unused style overrides
  const usedIds = new Set(newCharStyleOverrides.filter(id => id && id !== 0));
  const cleanedStyleOverrides = mergedStyleOverrides.filter(s => usedIds.has(s.id));

  return {
    text: newText,
    charStyleOverrides: newCharStyleOverrides,
    styleOverrides: cleanedStyleOverrides,
    css: current.css
  };
}

/**
 * Create clipboard data from current selection
 * @param {Object} htmlText - Current html_text state
 * @param {number} start - Selection start
 * @param {number} end - Selection end
 * @returns {string} JSON string for clipboard
 */
export function createClipboardData(htmlText, start, end) {
  const extracted = extractTextAndStyles(htmlText, start, end);
  return JSON.stringify(extracted);
}

/**
 * Sanitize text from external sources
 * Strips HTML tags, decodes entities, removes invisible characters
 * @param {string} text - Raw text that may contain HTML or special characters
 * @returns {string} Clean plain text
 */
function sanitizeText(text) {
  if (!text) return '';

  let clean = text;

  // If text contains HTML tags, extract just the text content
  if (/<[^>]+>/.test(clean)) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = clean;
    clean = tempDiv.textContent || tempDiv.innerText || '';
  }

  // Decode common HTML entities
  clean = clean
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  // Remove zero-width and invisible Unicode characters
  clean = clean.replace(/[\u200B-\u200D\uFEFF\u00AD\u200E\u200F]/g, '');

  // Normalize line breaks
  clean = clean.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Remove control characters (except newline \n and tab \t)
  clean = clean.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');

  return clean;
}

/**
 * Parse clipboard data, handling both styled and plain text
 * @param {ClipboardEvent} event - The paste event
 * @returns {Object} Parsed clipboard data with text and optional styles
 */
export function parseClipboardData(event) {
  // Try to get our styled JSON format first (internal copy/paste)
  const jsonData = event.clipboardData.getData('application/json');
  if (jsonData) {
    try {
      const parsed = JSON.parse(jsonData);
      if (parsed && typeof parsed.text === 'string') {
        // Internal paste - trust the structure but sanitize the text
        const sanitized = sanitizeText(parsed.text);
        if (sanitized.length !== parsed.text.length) {
          // Text length changed after sanitization, reset style overrides
          return {
            text: sanitized,
            charStyleOverrides: Array(sanitized.length).fill(0),
            styleOverrides: [],
            css: parsed.css || {}
          };
        }
        return parsed;
      }
    } catch (e) {
      // Invalid JSON, fall through to plain text
    }
  }

  // Fall back to plain text - sanitize external content
  const plainText = event.clipboardData.getData('text/plain');
  if (plainText) {
    const sanitized = sanitizeText(plainText);
    return {
      text: sanitized,
      charStyleOverrides: Array(sanitized.length).fill(0),
      styleOverrides: [],
      css: {}
    };
  }

  // Last resort: try to extract text from HTML
  const htmlData = event.clipboardData.getData('text/html');
  if (htmlData) {
    const sanitized = sanitizeText(htmlData);
    return {
      text: sanitized,
      charStyleOverrides: Array(sanitized.length).fill(0),
      styleOverrides: [],
      css: {}
    };
  }

  return null;
}

/**
 * Handle cut operation - returns extracted data and modified state
 * @param {Object} htmlText - Current html_text state
 * @param {number} start - Selection start
 * @param {number} end - Selection end
 * @returns {{ clipboardData: Object, newState: Object }}
 */
export function handleCut(htmlText, start, end) {
  const clipboardData = extractTextAndStyles(htmlText, start, end);

  // Remove the selected text
  const newState = mergeClipboardContent(htmlText, { text: '' }, start, end - start);

  return {
    clipboardData,
    newState
  };
}
